import { faCalendarDays, faClock } from '@fortawesome/free-regular-svg-icons'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import { useEffect, useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import ActionButtons from '@/components/ActionButtons'
import Address from '@/components/Address'
import BespokeCriteriaCard from '@/components/BespokeCriteriaCard'
import DetailPill from '@/components/DetailPill'
import Details from '@/components/Details'
import DetailsCard from '@/components/DetailsCard'
import DocumentCentre from '@/components/DocumentCentre'
import AdditionalDetailsCard from '@/components/EntityPanels/AdditionalDetailsCard/AdditionalDetailsCard'
import CreditCard from '@/components/EntityPanels/CreditCard/CreditCard'
import OrganisationalDetailsCard from '@/components/EntityPanels/OrganisationalDetails'
import OrganisationFinanceCard from '@/components/EntityPanels/OrganisationFinance'
import ResidentialCard from '@/components/EntityPanels/ResidentialCard/ResidentialCard'
import EscalationWarning from '@/components/Escalation/Warning'
import ExpectedError from '@/components/ExpectedError'
import {
  GoodlordControlBanner,
  showGoodlordControlBanner,
  isSelfServeStudent,
} from '@/components/GoodlordControl'
import { Header } from '@/components/Header'
import IdentityCard from '@/components/IdentityCard/IdentityCard'
import IncomePanel from '@/components/IncomeCard/IncomeCard'
import OverallOutcomeCard from '@/components/OverallOutcomeCard'
import SanctionsBanner from '@/components/Sanctions/Banner'
import { showSanctionsBanner } from '@/components/Sanctions/utils'
import SupportContactBanner from '@/components/SupportContacts/Banner'
import { hasActiveSupportContacts } from '@/components/SupportContacts/utils'
import VerifAICard from '@/components/VerifAICard'
import useApplicant from '@/hooks/useApplicant'
import useTouchpoints from '@/hooks/useTouchpoints'
import useUser from '@/hooks/useUser'
import useVerifAi from '@/hooks/useVerifAi'
import { usePatchFlagFeedbackMutation } from '@/lib/apollo'
import type { FlagFeedbackPayload } from '@/lib/apollo/mutation/patchFlagFeedback/mutation'
import {
  EVENT_TYPES,
  useSignalREventHandler,
} from '@/lib/signalR/hooks/useSignalREventHandler'
import type {
  EventHandler,
  ShouldHandleEvent,
} from '@/lib/signalR/hooks/useSignalREventHandler'
import { JourneyType } from '@/types/Activity'
import {
  toAgeOfReferenceLabel,
  hasAgeOfReferenceWarning,
  hasReopenAgeOfReferenceWarning,
} from '@/utils/ageOfReference'
import { isBespokeCriteriaGroup } from '@/utils/bespoke-criteria'
import { formatDate, parseDate } from '@/utils/date'
import { isRestrictedReference } from '@/utils/referenceRestrictions'
import { isCorporateTenant, isGuarantor, isTenant } from '@/utils/subjectHelpers'
import { useUserTracking } from '@/utils/tracking'

interface Params {
  subjectId: string
}

const PageWrapper = styled.div`
  height: 100%;
`

const PageContainer = styled.div`
  position: relative;
  display: block;
  padding: 10px 20px;
`

export const Layout = styled.div`
  display: grid;
  grid-template-rows: min-content 1fr;
  grid-row-gap: 10px;
  height: 100%;
  min-height: 0;
`

export const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 0;
`

export const ColumnContainer = styled.div`
  overflow-y: auto;
  min-height: 0;
`

export const PanelContainer = styled.div`
  margin-top: 10px;
`

const DetailPillsContainer = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 185px 165px 165px 165px;
`

const ApplicantPage = () => {
  const history = useHistory()

  const { subjectId } = useParams<Params>()

  const { customerId, isDeveloperModeEnabled, personId, skillLevel } = useUser()

  const { isLoadingTouchpoints, subjectTouchpoints, fetchSubjectTouchpoints } =
    useTouchpoints()

  const { loading, error, application, applicant, fetchSingleApplication } =
    useApplicant()

  const [requestsSidebarOpen, setRequestsSidebarOpen] = useState(false)

  const {
    fetchVerifAIResults,
    data: verifAIData,
    loading: loadingVerifAIFlags,
  } = useVerifAi()

  const [
    flagFeedbackMutation,
    { loading: loadingFlagFeedback, error: flagFeedbackError },
  ] = usePatchFlagFeedbackMutation()

  const submitFlagFeedback = (flagId: string, payload: FlagFeedbackPayload) =>
    flagFeedbackMutation({
      variables: {
        customerId,
        flagId,
        payload,
      },
    })
      .then(() => {
        fetchVerifAIResults(customerId, subjectId)
      })
      .catch(err => console.error('Error submitting flag feedback:', err))

  const isVerifAIEnabled = isDeveloperModeEnabled || skillLevel >= 6

  const onClose = () => history.push('/applicants')

  const { trackUserActivity } = useUserTracking()

  useEffect(() => {
    if (!isVerifAIEnabled || !customerId || !subjectId) {
      return
    }

    fetchVerifAIResults(customerId, subjectId)
  }, [fetchVerifAIResults, customerId, subjectId, isVerifAIEnabled])

  useEffect(() => {
    if (!customerId || !subjectId) {
      return
    }

    fetchSingleApplication(customerId, subjectId)

    trackUserActivity('View applicant', {
      subjectId,
      customerId,
    })
  }, [fetchSingleApplication, trackUserActivity, subjectId, customerId])

  useEffect(() => {
    if (!application) {
      return
    }

    fetchSubjectTouchpoints(application)
  }, [fetchSubjectTouchpoints, application])

  const shouldHandleSignalREvent = useCallback<ShouldHandleEvent>(
    event => application?.subjectId === event.SubjectId,
    [application],
  )

  const handleSignalREvent = useCallback<EventHandler>(
    event => {
      switch (event.EventType) {
        case EVENT_TYPES.TouchpointCreated:
          fetchSubjectTouchpoints(application)
          break
        case EVENT_TYPES.ReportGenerated:
        case EVENT_TYPES.SubjectOutcomeOverridden:
        case EVENT_TYPES.SubjectReopened:
        case EVENT_TYPES.SubjectOutcomeUpdated:
        case EVENT_TYPES.SubjectUpdated:
        case EVENT_TYPES.SubjectSectionReset:
        case EVENT_TYPES.SubjectSupportContactCreated:
        case EVENT_TYPES.SubjectSupportContactUpdated:
          fetchSingleApplication(event.CustomerId, event.SubjectId)
          break
      }
    },
    [application, fetchSubjectTouchpoints, fetchSingleApplication],
  )

  useSignalREventHandler({
    shouldHandleEvent: shouldHandleSignalREvent,
    eventHandler: handleSignalREvent,
  })

  const breadcrumbs = [
    {
      label: 'Applications',
      onClick: onClose,
    },
    {
      label: 'Applicant',
    },
  ]

  const referenceGroup = application?.journeyConfig?.referenceGroup

  const joinedQueueAt =
    applicant && applicant.joinedQueueAt !== null
      ? parseDate(applicant?.joinedQueueAt)
      : undefined
  const joinedReopenQueueAt =
    applicant && applicant.joinedReopenQueueAt !== null
      ? parseDate(applicant?.joinedReopenQueueAt)
      : undefined

  const onApplicantUpdated = () => fetchSingleApplication(customerId, subjectId)

  const isCorporateTenantSubject = isCorporateTenant(
    application?.type,
    application?.context?.journeyType,
  )

  const isSelfServeStudentSubject = isSelfServeStudent(applicant?.referenceTier)

  const shouldShowIncomePanel =
    application?.context?.journeyType !== undefined &&
    application?.context?.journeyType !== JourneyType.essential &&
    !isCorporateTenantSubject &&
    !isSelfServeStudentSubject

  const shouldShowCreditPanel = !isCorporateTenantSubject && !isSelfServeStudentSubject

  const isDataCollection =
    application?.context?.journeyType === JourneyType.dataCollection

  const isRestrictedReferenceTier = isRestrictedReference(applicant?.referenceTier)

  if (error !== undefined) {
    return (
      <ExpectedError
        text={error}
        imagePath="/assets/svg/failed-to-retrieve-applicant.svg"
      />
    )
  }

  if (isDataCollection && !isDeveloperModeEnabled) {
    return (
      <ExpectedError
        text="This is a data collection journey"
        subtext="No information available or actions required in RUDI"
        imagePath="/assets/svg/data-collection-journey.svg"
      />
    )
  }

  return (
    <PageWrapper>
      <Header
        isLoading={loading}
        hasError={error !== undefined}
        breadcrumbs={breadcrumbs}
        name={applicant?.fullName}
        subjectId={applicant?.id}
        avatar={applicant?.avatar ?? undefined}
        applicantType={applicant?.type}
        applicantTier={applicant?.referenceTier}
        lastProcessedBy={applicant?.lastProcessedBy ?? undefined}
        lastProcessedAt={applicant?.lastProcessedAt ?? undefined}
      />
      <PageContainer>
        {loading
          ? 'Loading...'
          : application &&
            applicant &&
            (!isTenant(application.type) && !isGuarantor(application.type) ? (
              <>The subject is not a tenant or guarantor.</>
            ) : (
              <Layout>
                <Details
                  application={application}
                  detailPills={
                    <DetailPillsContainer>
                      <DetailPill
                        icon={faCalendarDays}
                        title="Tenancy start date"
                        detail={formatDate(parseDate(applicant.tenancyStartDate))}
                      />
                      <DetailPill
                        icon={faClock}
                        title="Age of reference"
                        detail={toAgeOfReferenceLabel(joinedQueueAt)}
                        warning={hasAgeOfReferenceWarning(
                          joinedQueueAt,
                          joinedReopenQueueAt,
                        )}
                      />
                      {joinedReopenQueueAt !== undefined && (
                        <DetailPill
                          icon={faRotate}
                          title="Age since reopened"
                          detail={toAgeOfReferenceLabel(joinedReopenQueueAt)}
                          warning={hasReopenAgeOfReferenceWarning(joinedReopenQueueAt)}
                        />
                      )}
                    </DetailPillsContainer>
                  }
                  actionButtons={
                    <ActionButtons
                      applicant={applicant}
                      application={application}
                      onApplicantUpdated={onApplicantUpdated}
                      isRestrictedReference={isRestrictedReferenceTier}
                      requestsSidebarOpen={requestsSidebarOpen}
                      setRequestsSidebarOpen={setRequestsSidebarOpen}
                    />
                  }
                />
                {applicant.escalation !== null && (
                  <EscalationWarning
                    reason={applicant.escalation.reason}
                    authorId={applicant.escalation.author.userId}
                    fullName={applicant.escalation.author.fullName}
                    subjectId={applicant.id}
                    onApplicantUpdated={onApplicantUpdated}
                  />
                )}
                {hasActiveSupportContacts(applicant.supportContacts) && (
                  <SupportContactBanner
                    supportContacts={applicant.supportContacts}
                    applicantFirstName={applicant.firstName}
                  />
                )}
                {showSanctionsBanner(applicant.sanctions) && <SanctionsBanner />}
                {showGoodlordControlBanner(applicant.referenceTier) && (
                  <GoodlordControlBanner referenceTier={applicant.referenceTier} />
                )}
                <Container>
                  <ColumnContainer>
                    {isVerifAIEnabled && verifAIData !== undefined && (
                      <PanelContainer>
                        <VerifAICard
                          userId={personId}
                          subjectId={subjectId}
                          submitFlagFeedback={payload =>
                            submitFlagFeedback(verifAIData.id, payload)
                          }
                          loadingFlagFeedback={loadingFlagFeedback || loadingVerifAIFlags}
                          flagFeedbackError={flagFeedbackError}
                          flags={verifAIData.flags ?? []}
                        />
                      </PanelContainer>
                    )}
                    <PanelContainer>
                      <DetailsCard
                        applicationId={applicant.applicationId}
                        subjectId={applicant.id}
                        agencyName={applicant.agencyName}
                        onClose={onClose}
                        application={application}
                        fetchSingleApplication={fetchSingleApplication}
                        isRestrictedReference={isRestrictedReferenceTier}
                      >
                        <h5>Applying for</h5>
                        <Address {...applicant.addressApplyingFor} />
                        <h5>Squad</h5>
                        {applicant.swimLaneGroup}
                        <h5>Reference group</h5>
                        {applicant.referenceGroup}
                      </DetailsCard>
                    </PanelContainer>
                    <PanelContainer>
                      <IdentityCard
                        applicant={applicant}
                        application={application}
                        fetchSingleApplication={fetchSingleApplication}
                        isLoadingTouchpoints={isLoadingTouchpoints}
                        subjectTouchpoints={subjectTouchpoints}
                        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                        isRestrictedReference={isRestrictedReferenceTier}
                      />
                    </PanelContainer>
                    {shouldShowCreditPanel && (
                      <PanelContainer>
                        <CreditCard
                          subjectTouchpoints={subjectTouchpoints}
                          application={application}
                          isLoadingTouchpoints={isLoadingTouchpoints}
                          fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                          applicant={applicant}
                          isRestrictedReference={isRestrictedReferenceTier}
                        />
                      </PanelContainer>
                    )}
                    {shouldShowIncomePanel && (
                      <PanelContainer>
                        <IncomePanel
                          fetchSingleApplication={fetchSingleApplication}
                          incomes={application.context?.incomes ?? []}
                          application={application}
                          isLoadingTouchpoints={isLoadingTouchpoints}
                          subjectTouchpoints={subjectTouchpoints}
                          fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                          onApplicantUpdated={onApplicantUpdated}
                          isRestrictedReference={isRestrictedReferenceTier}
                          onOpenRequests={() => setRequestsSidebarOpen(true)}
                        />
                      </PanelContainer>
                    )}
                    {!isCorporateTenantSubject && (
                      <PanelContainer>
                        <ResidentialCard
                          application={application}
                          isLoadingTouchpoints={isLoadingTouchpoints}
                          subjectTouchpoints={subjectTouchpoints}
                          fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                          isRestrictedReference={isRestrictedReferenceTier}
                        />
                      </PanelContainer>
                    )}
                    {isCorporateTenantSubject && (
                      <OrganisationalDetailsCard
                        application={application}
                        isLoadingTouchpoints={isLoadingTouchpoints}
                        subjectTouchpoints={subjectTouchpoints}
                        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                      />
                    )}
                    {isCorporateTenantSubject && (
                      <OrganisationFinanceCard
                        application={application}
                        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
                        isLoadingTouchpoints={isLoadingTouchpoints}
                        subjectTouchpoints={subjectTouchpoints}
                      />
                    )}
                    {(application.context?.emergencyContactDetails ||
                      (application.additionalQuestions?.items &&
                        application.additionalQuestions.items.length > 0)) && (
                      <PanelContainer>
                        <AdditionalDetailsCard application={application} />
                      </PanelContainer>
                    )}
                  </ColumnContainer>
                  <ColumnContainer>
                    <OverallOutcomeCard application={application} />
                    {isBespokeCriteriaGroup(referenceGroup) && (
                      <PanelContainer>
                        <BespokeCriteriaCard referenceGroup={referenceGroup} />
                      </PanelContainer>
                    )}
                    {application.context && (
                      <PanelContainer>
                        <DocumentCentre
                          application={application}
                          fetchSingleApplication={fetchSingleApplication}
                        />
                      </PanelContainer>
                    )}
                  </ColumnContainer>
                </Container>
              </Layout>
            ))}
      </PageContainer>
    </PageWrapper>
  )
}

export default ApplicantPage
